// ---------------------------------------------------------------------------
// Portraits for AI employees. Made with GPT Image 2.5 Flare by
// scripts/generate-employee-portraits.cjs and served from R2 (images bucket):
//   <base>/<key>.webp      256 px, cropped for round avatars
//   <base>/<key>-512.webp  512 px, the full frame
// Keys are template slugs (each template's own face) plus a general set
// (pNN) for employees made from scratch. An employee's `avatar` is either
// one of these URLs or an emoji.
//
// Files never change in place: a changed or extended set is uploaded under
// a new version FIRST, then EMPLOYEE_PHOTO_VERSION is bumped (Cloudflare
// caches a 404, so nothing may point at a key before it exists). Older
// versions stay valid for employees that saved them.
// Client-safe: no imports.
// ---------------------------------------------------------------------------

export const EMPLOYEE_PHOTO_VERSION = 'v1';

const HOST = 'https://media.easy-peasy.ai';
const PREFIX = '/employees/avatars/';

/** One portrait per template (key = template slug). */
export const TEMPLATE_PHOTO_KEYS = [
  'chief-of-staff',
  'support-rep',
  'marketing-creator',
  'business-analyst',
  'executive-assistant',
  'seo-writer',
  'research-assistant',
  'social-media-manager',
  'email-marketer',
  'sales-development-rep',
  'customer-success-manager',
  'product-manager',
  'project-manager',
  'data-analyst',
  'finance-assistant',
  'recruiter',
  'designer',
  'video-producer',
  'localization',
] as const;

/**
 * Faces for employees made from scratch. `label` (gender and age only, on
 * purpose) lets the drafting model pick a face that fits the name it chose.
 */
export const GENERAL_PHOTOS = [
  { key: 'p01', label: 'man, mid 20s' },
  { key: 'p02', label: 'woman, mid 40s' },
  { key: 'p03', label: 'woman, late 50s, silver hair' },
  { key: 'p04', label: 'man, mid 40s, beard' },
  { key: 'p05', label: 'woman, mid 20s, curly hair' },
  { key: 'p06', label: 'man, early 30s' },
  { key: 'p07', label: 'woman, early 60s' },
  { key: 'p08', label: 'man, mid 20s' },
  { key: 'p09', label: 'man, early 30s, red hair' },
  { key: 'p10', label: 'woman, mid 30s' },
  { key: 'p11', label: 'man, mid 40s, shaved head' },
  { key: 'p12', label: 'woman, early 30s, glasses' },
] as const;

export type GeneralPhotoKey = (typeof GENERAL_PHOTOS)[number]['key'];
export type EmployeePhotoKey =
  | (typeof TEMPLATE_PHOTO_KEYS)[number]
  | GeneralPhotoKey;

export const GENERAL_PHOTO_KEYS = GENERAL_PHOTOS.map(
  (photo) => photo.key,
) as GeneralPhotoKey[];

const KNOWN_KEYS = new Set<string>([
  ...TEMPLATE_PHOTO_KEYS,
  ...GENERAL_PHOTO_KEYS,
]);

export const DEFAULT_EMPLOYEE_AVATAR = '🤖';

/** Longest emoji avatar kept (ZWJ sequences, flags, skin tones). */
const MAX_EMOJI_AVATAR_CHARS = 16;

export function employeePhotoUrl(
  key: EmployeePhotoKey,
  size: 256 | 512 = 256,
): string {
  return `${HOST}${PREFIX}${EMPLOYEE_PHOTO_VERSION}/${key}${
    size === 512 ? '-512' : ''
  }.webp`;
}

const PHOTO_URL_RE =
  /^https:\/\/media\.easy-peasy\.ai\/employees\/avatars\/v\d{1,3}\/([a-z0-9-]+?)(-512)?\.webp$/;

/** The portrait key of one of our portrait URLs, else null. */
export function employeePhotoKey(value: unknown): EmployeePhotoKey | null {
  const match = PHOTO_URL_RE.exec(String(value ?? '').trim());
  if (!match || !KNOWN_KEYS.has(match[1])) return null;
  return match[1] as EmployeePhotoKey;
}

/** True for one of our portraits (any version). Never any other URL. */
export function isEmployeePhotoUrl(value: unknown): value is string {
  return employeePhotoKey(value) !== null;
}

/** The larger, full-frame copy of a portrait (for big displays). */
export function employeePhotoLargeUrl(value: string): string | null {
  const trimmed = String(value || '').trim();
  if (!isEmployeePhotoUrl(trimmed)) return null;
  return trimmed.replace(/(-512)?\.webp$/, '-512.webp');
}

/**
 * What may be stored as an employee's avatar: one of our portrait URLs or a
 * short emoji. Anything else (other URLs, long text) → null.
 */
export function normalizeEmployeeAvatar(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) || trimmed.includes('/')) {
    return isEmployeePhotoUrl(trimmed) ? trimmed : null;
  }
  if (/\s/.test(trimmed) || trimmed.length > MAX_EMOJI_AVATAR_CHARS) {
    return null;
  }
  return trimmed;
}

/**
 * A general-set face for a new employee, skipping faces the owner's other
 * employees already have. `seed` makes the pick stable for one draft.
 */
export function pickGeneralPhoto(
  taken: Iterable<string | null | undefined> = [],
  seed = 0,
): string {
  const used = new Set<string>();
  for (const avatar of taken) {
    const key = employeePhotoKey(avatar);
    if (key) used.add(key);
  }
  const free = GENERAL_PHOTO_KEYS.filter((key) => !used.has(key));
  const pool = free.length ? free : GENERAL_PHOTO_KEYS;
  const index = Math.abs(Math.floor(seed)) % pool.length;
  return employeePhotoUrl(pool[index]);
}
